'use client'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/core/components/accordion'
import { Button } from '@/core/components/button'

const sections = [
  {
    id: 'businesses',
    title: 'HubHive for Businesses',
    description:
      'Connect directly with your local community and build lasting customer relationships.',
    features: [
      'Direct community engagement',
      'Exclusive member deals',
      'Trusted customer relationships',
      'Local market reach',
    ],
    emoji: '🏢',
    ctaText: 'Start Selling in Communities',
  },
  {
    id: 'members',
    title: 'HubHive for Community Members',
    description:
      'Discover products and services from trusted neighbors, participate in local events.',
    features: [
      'Trusted community recommendations',
      'Local events and networking',
      'Support neighborhood businesses',
      'Exclusive member benefits',
    ],
    emoji: '👥',
    ctaText: 'Join Your Community',
  },
  {
    id: 'admins',
    title: 'HubHive for Community Admins',
    description:
      'Build and manage thriving communities with powerful tools for moderation.',
    features: [
      'Advanced moderation tools',
      'Privacy and content controls',
      'Community analytics',
      'Member engagement features',
    ],
    emoji: '⚙️',
    ctaText: 'Create Your Hive',
  },
] as const

export function WhoItsFor() {
  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-navy mb-4">
            Built for Every Part of Your Community
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Whether you&apos;re running a business, managing a community, or just looking to connect
            with neighbors.
          </p>
        </div>
        <div className="max-w-6xl mx-auto">
          <div className="border border-border rounded-2xl bg-background shadow-card overflow-hidden">
            <Accordion type="single" collapsible>
              {sections.map((section, index) => (
                <AccordionItem
                  key={section.id}
                  value={section.id}
                  className={index !== sections.length - 1 ? 'border-b border-border' : ''}
                >
                  <AccordionTrigger className="px-8 py-6 text-left hover:no-underline hover:bg-muted/50 transition-colors">
                    <h3 className="text-xl lg:text-2xl font-semibold text-navy">{section.title}</h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-8 pb-8 bg-muted/20">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div className="space-y-6">
                        <p className="text-lg text-muted-foreground leading-relaxed">
                          {section.description}
                        </p>
                        <ul className="space-y-3">
                          {section.features.map((feature) => (
                            <li key={feature} className="flex items-center space-x-3">
                              <div className="w-2 h-2 bg-accent rounded-full flex-shrink-0" />
                              <span className="text-muted-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <Button variant="gradient" size="lg" className="mt-6">
                          {section.ctaText}
                        </Button>
                      </div>
                      <div className="lg:order-last">
                        <div className="relative mx-auto max-w-lg">
                          <div className="aspect-[4/3] bg-gradient-subtle rounded-xl border-2 border-dashed border-muted flex items-center justify-center">
                            <div className="text-center space-y-4 p-8">
                              <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center text-2xl mx-auto">
                                {section.emoji}
                              </div>
                              <h4 className="font-semibold text-navy mb-2">{section.title}</h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  )
}
